import React from 'react'

import {
  getPaymentTypeTagColor,
  STATUS_PILL_COLOR_MAP,
} from '../../PaymentAssignment/payment-assignment.style'
import {
  PAYMENT_TYPE_LABELS,
  PaymentType,
} from '../../PaymentAssignment/Utils/types'
import { formatPaymentLabel } from './helpers'
import styles from './pills.module.scss'

/**
 * Shared presentational atoms for the BenefitsTable subtree. Self-contained
 * (no CSS selector reaches outside these components), so they own their own
 * stylesheet. Dynamic states are expressed as modifier classes composed with
 * template literals (there is no clsx in this codebase).
 */

/** Which provider-pill color variant a source maps to. */
export type ProviderPillVariant = 'marqeta' | 'wex' | 'neutral'

export const CountBadge: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => <span className={styles.countBadge}>{children}</span>

export const BadgeBubble: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => <span className={styles.badgeBubble}>{children}</span>

/** M = Marqeta (purple), W = WEX (blue); everything else is a neutral pill. */
export const ProviderPill: React.FC<{
  variant: ProviderPillVariant
  title?: string
  children: React.ReactNode
}> = ({ variant, title, children }) => (
  <span className={`${styles.providerPill} ${styles[variant]}`} title={title}>
    {children}
  </span>
)

/**
 * Payment-type chip (Auto / Manual / Bot) — background + text color match the
 * Payment Assignment page tags via STATUS_PILL_COLOR_MAP (the shared cross-page
 * source of truth), applied inline. Only its layout comes from the stylesheet.
 */
export const PaymentTypeChip: React.FC<{
  type: string
  style?: React.CSSProperties
}> = ({ type, style }) => {
  const { backgroundColor, textColor } =
    STATUS_PILL_COLOR_MAP[getPaymentTypeTagColor(type)]
  const label =
    PAYMENT_TYPE_LABELS[type.toUpperCase() as PaymentType] ??
    formatPaymentLabel(type)
  return (
    <span
      className={styles.paymentTypePill}
      title={`Payment type: ${label}`}
      style={{ background: backgroundColor, color: textColor, ...style }}
    >
      {label}
    </span>
  )
}

/** Red "(R)" suffix on a source pill whose source has a refund in the cell. */
export const RefundMark: React.FC = () => (
  <span className={styles.refundMark}>(R)</span>
)

export const CopyBtn: React.FC<{
  onClick: () => void
  'aria-label'?: string
  children: React.ReactNode
}> = ({ onClick, children, ...rest }) => (
  <button
    type="button"
    className={styles.copyBtn}
    onClick={onClick}
    aria-label={rest['aria-label']}
  >
    {children}
  </button>
)

export const TerminatedBadge: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => <span className={styles.terminatedBadge}>{children}</span>

/** Checkbox square used by BOTH ColumnsMenu and FiltersDrawer. */
export const CheckSquare: React.FC<{
  on: boolean
  children?: React.ReactNode
}> = ({ on, children }) => (
  <span className={`${styles.checkSquare}${on ? ` ${styles.on}` : ''}`}>
    {children}
  </span>
)

/**
 * An icon-sized anchor that leaves this page, styled to sit beside {@link
 * CopyBtn} in a cell — same faint-until-hover treatment, so a row's small
 * controls read as one set rather than as a button next to a link.
 *
 * Always a real anchor with `target="_blank"`: these destinations open in a new
 * tab so the operator keeps their place in the table, and middle-click / "open
 * in new window" have to work the way they do on the names beside them.
 */
export const IconLink: React.FC<{
  href: string
  label: string
  children: React.ReactNode
}> = ({ href, label, children }) => (
  <a
    className={styles.iconLink}
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    title={label}
    aria-label={label}
  >
    {children}
  </a>
)
